import moment from 'moment';
import {List, Record, Map} from 'immutable';

/**
 * Provide some definitions on grouping types and translations
 *
 * The keys in this map will be used to drive grouping, if the type
 * doesn't exist in this map, we will not attempt to group (see shouldGroup below)
 *
 * Translation - this type from the server becomes this other type
 *  on the front end post-grouping.  No translation = use the server type
 */
const typeGroups = {
  comment_given: {
    translate_to: 'feedback_provided',
  },
  highlight_given: {
    translate_to: 'feedback_provided',
  },
  comment_received: {
    translate_to: 'feedback_received',
  },
  highlight_received: {
    translate_to: 'feedback_received',
  },
  trigger_notification: {},
};

/**
 * if grouped, change the description using the following translation
 */
const overrideDescriptions = {
  feedback_provided: 'You provided feedback on a call',
  feedback_received: 'You received feedback on a call',
};

/**
 * possibly translate the type (group like items)
 *
 * @param type
 * @return translated type
 */
const determineGroupedTypeFor = type => {
  const translatedType = typeGroups[type] && typeGroups[type].translate_to;
  return translatedType || type;
};

/**
 * Group rows if two things are true -
 *  - it is a "grouping type" - don't group everything!
 *  - it matches an optional "context match"
 * @param item
 * @return {boolean}
 */
const shouldGroup = item => Object.keys(typeGroups).includes(item.context.type);

/**
 * Group like items into one row, preserving individual item context
 * so we can always get back to them in order to mark as read, move folder, etc
 */
export const groupItems = folderData => {
  const GroupKey = Record({
    key: '',
    groupedType: '',
    date: '',
  });

  const [groupingMap, rest] = folderData.items.reduce(
    ([groupingMap, rest], item) => {
      if (shouldGroup(item)) {
        const key = new GroupKey({
          key: item.call.id,
          groupedType: determineGroupedTypeFor(item.context.type),
          date: moment(item.created).format('MMDDYY'),
        });

        const updatedMap = groupingMap.update(key, val =>
          val === undefined ? List([item]) : val.push(item)
        );

        return [updatedMap, rest];
      } else {
        return [groupingMap, rest.push(item)];
      }
    },
    [Map(), List()]
  );

  const items = groupingMap.mapEntries(([key, value]) => {
    const f = value.get(0);

    // if it turns out we only had 1 in our bucket, just return it. dont group.
    if (value.size === 1) return [key, f];

    // if any were unviewed, let's make the whole group unviewed
    const anyInGroupUnviewed = value.some(i => !i.viewedDate);

    // create an aggregate holder for the group
    const groupedValue = {
      ...f,
      // if any where pinned, make the whole group pinned
      pinned: value.some(i => i.pinned),
      viewedDate: anyInGroupUnviewed ? null : value.find(i => i.viewedDate),
      useNestedDescriptions: key.groupedType === 'trigger_notification',
      context: {
        ...f.context,
        type: typeGroups[f.context.type].translate_to || f.context.type,
        description: overrideDescriptions[key.groupedType] || f.context.description,
      },
    };

    return [
      key,
      {
        ...groupedValue,
        isGrouped: true,
        groupedItems: value.map(v => {
          return {...v, isGrouped: true};
        }),
      },
    ];
  });

  const final = items
    .toList()
    .concat(rest)
    .sort((l, r) => new Date(r.created) - new Date(l.created));

  return {
    ...folderData,
    items: final,
  };
};
